import { useEffect, useState } from "react";
import { API } from "../lib/api";
import { QrCode, DownloadSimple, ArrowsClockwise, WarningCircle, CircleNotch, PencilSimple } from "@phosphor-icons/react";

// Reusable QR card. The QR is built live from the current origin, so it always
// reflects the URL the site is served from. "Refresh" re-pulls it (useful after
// the site is deployed to its final URL). Controls (download/refresh) are hidden
// behind a small edit toggle so only the QR shows by default.
export default function QrCard({ apiPath, targetUrl, downloadName, title = "Scan to open", description, testid = "qr-card" }) {
  const [qrUrl, setQrUrl] = useState("");
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [open, setOpen] = useState(false);
  const origin = window.location.origin;

  useEffect(() => {
    let url;
    setError(false);
    setQrUrl("");
    const sep = apiPath.includes("?") ? "&" : "?";
    fetch(`${API}${apiPath}${sep}base=${encodeURIComponent(origin)}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("qr"); return r.blob(); })
      .then((b) => { url = URL.createObjectURL(b); setQrUrl(url); setRefreshedAt(new Date()); })
      .catch(() => setError(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [apiPath, origin, attempt]);

  const refreshedLabel = refreshedAt
    ? refreshedAt.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="flex items-start gap-5 rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="flex h-32 w-32 shrink-0 items-center justify-center rounded-sm border border-neutral-200 bg-white">
        {error
          ? <WarningCircle size={26} className="text-[#D97706]" />
          : qrUrl
            ? <img src={qrUrl} alt="QR code" className="h-28 w-28" data-testid={`${testid}-image`} />
            : <CircleNotch size={24} className="animate-spin text-[#0055FF]" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-neutral-500">
            <QrCode size={16} weight="bold" />
            {title ? <span className="text-xs uppercase tracking-widest font-semibold">{title}</span> : null}
          </div>
          <button data-testid={`${testid}-edit-toggle`} onClick={() => setOpen((o) => !o)}
            title={open ? "Hide options" : "Edit / download options"} aria-label="QR options"
            className={`flex h-8 w-8 items-center justify-center rounded-sm border transition-all duration-200 ${open ? "border-[#0055FF] bg-[#0055FF]/[0.06] text-[#0055FF]" : "border-neutral-300 bg-white text-neutral-500 hover:border-[#0055FF] hover:text-[#0055FF]"}`}>
            <PencilSimple size={15} weight="bold" />
          </button>
        </div>

        {open && (
          <div className="mt-2" data-testid={`${testid}-controls`}>
            {description && <p className="text-sm text-neutral-600">{description}</p>}
            <p className="mt-1 break-all font-mono text-xs text-neutral-400">{targetUrl}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {error ? (
                <button data-testid={`${testid}-retry`} onClick={() => setAttempt((a) => a + 1)}
                  className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-3.5 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
                  <ArrowsClockwise size={15} weight="bold" /> Couldn't load QR — Retry
                </button>
              ) : (
                <>
                  <a data-testid={`${testid}-download`} href={qrUrl || undefined} download={downloadName}
                    className={`inline-flex items-center gap-2 rounded-sm bg-[#0055FF] px-3.5 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] ${qrUrl ? "" : "pointer-events-none opacity-50"}`}>
                    <DownloadSimple size={15} weight="bold" /> Download JPG
                  </a>
                  <button data-testid={`${testid}-refresh`} onClick={() => setAttempt((a) => a + 1)}
                    className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-3.5 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
                    <ArrowsClockwise size={15} weight="bold" /> Refresh QR
                  </button>
                  {refreshedLabel && (
                    <span className="text-xs text-neutral-500" data-testid={`${testid}-refreshed-at`}>
                      Last refreshed {refreshedLabel}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

